/**
 * Deal Heatmap — single map overlay of LEADS + cash BUYERS.
 *
 * Wholesalers can finally see their cold leads sitting next to hot cash
 * buyers in the same neighborhood and dial the best match without ever
 * leaving the page. Layer toggle lets them isolate either layer.
 *
 * Pin language:
 *   ◉  Leads layer — blue dot (default), amber for AI-scored
 *   ▼  Buyers layer — green/amber/gray teardrop by propensity, red+gold ring for distress
 *
 * Built on the same Leaflet/OSM stack as Discover & BuyerMapView so visual
 * consistency is automatic.
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { toast } from "sonner";
import {
  Layers, MapPin, Loader2, Gem, Flame, ShieldAlert, Users, Wallet,
  ArrowRight, AlertCircle,
} from "lucide-react";
import { api, fmtMoney } from "@/lib/api";


// ── Pin factories ────────────────────────────────────────────────────────
function teardropPin(color, ring) {
  const svg = `
<svg xmlns="http://www.w3.org/2000/svg" width="28" height="40" viewBox="0 0 28 40">
  <path d="M14 0C6.3 0 0 6.3 0 14c0 9.6 14 26 14 26s14-16.4 14-26C28 6.3 21.7 0 14 0z" fill="${color}"/>
  <circle cx="14" cy="14" r="5" fill="#fff"/>
  ${ring ? `<circle cx="14" cy="14" r="11" fill="none" stroke="${ring}" stroke-width="2.5"/>` : ""}
</svg>`;
  return L.divIcon({
    html: svg, className: "af-pin", iconSize: [28, 40],
    iconAnchor: [14, 40], popupAnchor: [0, -36],
  });
}

function dotPin(color, ring) {
  const svg = `
<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18">
  <circle cx="9" cy="9" r="7" fill="${color}" stroke="#fff" stroke-width="2"/>
  ${ring ? `<circle cx="9" cy="9" r="8" fill="none" stroke="${ring}" stroke-width="1.5"/>` : ""}
</svg>`;
  return L.divIcon({
    html: svg, className: "af-dot", iconSize: [18, 18],
    iconAnchor: [9, 9], popupAnchor: [0, -10],
  });
}

const PIN_BUYER_HOT = teardropPin("#10B981");
const PIN_BUYER_WARM = teardropPin("#F59E0B");
const PIN_BUYER_COLD = teardropPin("#94A3B8");
const PIN_BUYER_DISTRESS = teardropPin("#E11D48", "#FBBF24");

const PIN_LEAD = dotPin("#3B82F6");
const PIN_LEAD_HOT = dotPin("#10B981", "#FBBF24");      // AI-scored hot lead
const PIN_LEAD_NURTURE = dotPin("#A855F7");             // nurture/contacted

function pinForBuyer(b) {
  if (b.has_distress) return PIN_BUYER_DISTRESS;
  const s = b.sale_propensity_score ?? 0;
  if (s >= 80) return PIN_BUYER_HOT;
  if (s >= 60) return PIN_BUYER_WARM;
  return PIN_BUYER_COLD;
}

function pinForLead(l) {
  const stage = (l.stage || "").toLowerCase();
  if (stage === "hot" || stage === "qualified" || stage === "appointment") return PIN_LEAD_HOT;
  if (stage === "nurture" || stage === "contacted" || stage === "follow_up") return PIN_LEAD_NURTURE;
  return PIN_LEAD;
}


function MapRecenter({ center }) {
  const map = useMap();
  useEffect(() => {
    if (center && Number.isFinite(center[0]) && Number.isFinite(center[1])) {
      map.flyTo(center, 11, { duration: 0.9 });
    }
  }, [center, map]);
  return null;
}


export default function Heatmap() {
  const [data, setData] = useState({ leads: [], buyers: [], counts: { leads: 0, buyers: 0 } });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [market, setMarket] = useState("");
  const [showLeads, setShowLeads] = useState(true);
  const [showBuyers, setShowBuyers] = useState(true);
  const [activeBuyer, setActiveBuyer] = useState(null);
  const [activeLead, setActiveLead] = useState(null);

  const load = async (marketFilter = "") => {
    setLoading(true);
    setError(null);
    try {
      const params = marketFilter ? `?market=${encodeURIComponent(marketFilter)}` : "";
      const r = await api.get(`/heatmap${params}`);
      setData(r.data || { leads: [], buyers: [], counts: { leads: 0, buyers: 0 } });
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to load heatmap");
      toast.error("Could not load heatmap");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(""); }, []);

  // Center: average of visible points (with bias toward whichever layer is shown)
  const center = useMemo(() => {
    const visible = [];
    if (showLeads) visible.push(...data.leads);
    if (showBuyers) visible.push(...data.buyers);
    if (visible.length === 0) return [39.5, -98.5];
    const lat = visible.reduce((a, p) => a + p.latitude, 0) / visible.length;
    const lng = visible.reduce((a, p) => a + p.longitude, 0) / visible.length;
    return [lat, lng];
  }, [data, showLeads, showBuyers]);

  const handleMarketSubmit = (e) => {
    e.preventDefault();
    load(market.trim());
  };

  return (
    <div className="space-y-6" data-testid="heatmap-page">
      {/* Header */}
      <div>
        <div className="text-[10px] uppercase tracking-widest text-emerald-700 font-bold flex items-center gap-1.5">
          <Layers size={11} /> Deal Heatmap
        </div>
        <h1 className="font-display font-black text-3xl text-[var(--ink)] mt-1">
          Leads + Buyers · one map.
        </h1>
        <p className="text-sm text-[var(--ink-3)] mt-1 max-w-2xl">
          Every active lead overlaid with the cash buyers buying in that same neighborhood.
          Spot the match. Dial the deal.
        </p>
      </div>

      {/* Controls bar */}
      <div
        className="card p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3"
        data-testid="heatmap-controls"
      >
        <form onSubmit={handleMarketSubmit} className="flex items-center gap-2 flex-1 min-w-0">
          <MapPin size={16} className="text-[var(--ink-3)] shrink-0" />
          <input
            type="text"
            value={market}
            onChange={(e) => setMarket(e.target.value)}
            placeholder="Filter by market (e.g. Dallas, TX)"
            className="input flex-1 min-w-0"
            data-testid="heatmap-market-input"
          />
          <button type="submit" className="btn-money text-xs" data-testid="heatmap-apply">
            Apply
          </button>
        </form>

        <div className="flex items-center gap-1.5">
          <button
            type="button"
            onClick={() => setShowLeads((s) => !s)}
            className={`text-[10px] font-bold uppercase tracking-wider px-3 py-1.5 rounded-md border transition-colors flex items-center gap-1 ${
              showLeads
                ? "bg-blue-600 text-white border-blue-600"
                : "bg-white text-[var(--ink-3)] border-[var(--line)] hover:text-[var(--ink)]"
            }`}
            data-testid="heatmap-toggle-leads"
          >
            <Users size={11} /> Leads · {data.counts?.leads || 0}
          </button>
          <button
            type="button"
            onClick={() => setShowBuyers((s) => !s)}
            className={`text-[10px] font-bold uppercase tracking-wider px-3 py-1.5 rounded-md border transition-colors flex items-center gap-1 ${
              showBuyers
                ? "bg-emerald-600 text-white border-emerald-600"
                : "bg-white text-[var(--ink-3)] border-[var(--line)] hover:text-[var(--ink)]"
            }`}
            data-testid="heatmap-toggle-buyers"
          >
            <Wallet size={11} /> Buyers · {data.counts?.buyers || 0}
          </button>
        </div>
      </div>

      {/* Map */}
      {error ? (
        <div className="card p-6 text-sm text-rose-700 flex items-center gap-2" data-testid="heatmap-error">
          <AlertCircle size={16} /> {error}
        </div>
      ) : loading ? (
        <div className="card p-12 text-center text-[var(--ink-3)]" data-testid="heatmap-loading">
          <Loader2 className="animate-spin inline mr-2" size={16} /> Loading deal heatmap…
        </div>
      ) : data.counts?.leads === 0 && data.counts?.buyers === 0 ? (
        <div
          className="card p-10 text-center"
          data-testid="heatmap-empty"
        >
          <Layers size={40} className="mx-auto text-[var(--ink-3)] mb-3 opacity-60" />
          <div className="font-display font-bold text-lg text-[var(--ink)]">
            Your map is empty
          </div>
          <p className="text-sm text-[var(--ink-3)] mt-2 max-w-md mx-auto">
            {data.empty_state_hint || "Add leads from Discover or save buyers from Buyer Search to populate the map."}
          </p>
          <div className="flex items-center justify-center gap-2 mt-4">
            <Link to="/app/discover" className="btn-outline text-xs">
              <MapPin size={11} /> Find leads in Discover
            </Link>
            <Link to="/app/buyers/search" className="btn-money text-xs">
              <Wallet size={11} /> Search cash buyers
            </Link>
          </div>
        </div>
      ) : (
        <div className="card p-0 overflow-hidden" style={{ height: "70vh" }} data-testid="heatmap-map-wrap">
          <MapContainer
            center={center}
            zoom={data.leads.length + data.buyers.length ? 11 : 4}
            style={{ height: "100%", width: "100%" }}
            scrollWheelZoom
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            <MapRecenter center={center} />

            {/* Leads layer */}
            {showLeads && data.leads.map((l) => (
              <Marker
                key={`l-${l.id}`}
                position={[l.latitude, l.longitude]}
                icon={pinForLead(l)}
                eventHandlers={{ click: () => { setActiveLead(l); setActiveBuyer(null); } }}
              >
                <Popup>
                  <div className="text-xs" data-testid={`heatmap-lead-popup-${l.id}`}>
                    <div className="font-semibold mb-0.5">{l.property_address}</div>
                    <div className="text-[var(--ink-3)] mb-1">
                      {l.city}{l.state ? `, ${l.state}` : ""} · Stage: <strong>{l.stage}</strong>
                    </div>
                    {l.asking_price ? (
                      <div className="text-[var(--ink-2)]">{fmtMoney(l.asking_price * 100)}</div>
                    ) : null}
                    <Link
                      to={`/app/leads/${l.id}`}
                      className="mt-2 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded bg-blue-600 text-white"
                      data-testid={`heatmap-open-lead-${l.id}`}
                    >
                      Open lead <ArrowRight size={9} />
                    </Link>
                  </div>
                </Popup>
              </Marker>
            ))}

            {/* Buyers layer */}
            {showBuyers && data.buyers.map((b) => (
              <Marker
                key={`b-${b.id}`}
                position={[b.latitude, b.longitude]}
                icon={pinForBuyer(b)}
                eventHandlers={{ click: () => { setActiveBuyer(b); setActiveLead(null); } }}
              >
                <Popup>
                  <div className="text-xs" data-testid={`heatmap-buyer-popup-${b.id}`}>
                    <div className="flex items-center gap-1 mb-0.5">
                      <span className="font-semibold">{b.name}</span>
                      {b.is_millionaire && (
                        <span className="inline-flex items-center gap-0.5 text-[9px] uppercase font-bold tracking-wider px-1 py-[1px] rounded bg-gradient-to-r from-amber-400 to-yellow-500 text-white">
                          <Gem size={8} /> M
                        </span>
                      )}
                      {b.has_distress && (
                        <span className="inline-flex items-center gap-0.5 text-[9px] uppercase font-bold tracking-wider px-1 py-[1px] rounded bg-rose-600 text-white">
                          <ShieldAlert size={8} /> Seller?
                        </span>
                      )}
                    </div>
                    <div className="text-[var(--ink-3)] mb-1">
                      {b.primary_market} · {b.properties_owned} props
                    </div>
                    <div className="flex items-center gap-2 text-[var(--ink-2)]">
                      <span>
                        <Flame size={10} className="inline -mt-0.5 mr-0.5 text-emerald-600" />
                        {b.sale_propensity_score}/99
                      </span>
                      {b.net_worth_label && <span>NW {b.net_worth_label}</span>}
                    </div>
                    <Link
                      to="/app/buyers/search"
                      className="mt-2 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded bg-emerald-600 text-white"
                      data-testid={`heatmap-open-buyer-${b.id}`}
                    >
                      Open in search <ArrowRight size={9} />
                    </Link>
                  </div>
                </Popup>
              </Marker>
            ))}
          </MapContainer>
        </div>
      )}

      {/* Legend */}
      <div className="card p-4" data-testid="heatmap-legend">
        <div className="text-[10px] uppercase tracking-widest text-[var(--ink-3)] font-bold mb-2">
          Legend
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
          <LegendDot color="#3B82F6" label="Lead · default" />
          <LegendDot color="#10B981" ring="#FBBF24" label="Lead · hot / qualified" />
          <LegendDot color="#A855F7" label="Lead · nurture" />
          <LegendDot color="#94A3B8" label="Buyer · cold" teardrop />
          <LegendDot color="#F59E0B" label="Buyer · warm (60+)" teardrop />
          <LegendDot color="#10B981" label="Buyer · hot (80+)" teardrop />
          <LegendDot color="#E11D48" ring="#FBBF24" label="Buyer · distress (seller opp)" teardrop />
        </div>
      </div>
    </div>
  );
}

function LegendDot({ color, ring, label, teardrop }) {
  return (
    <div className="flex items-center gap-2">
      <div className="shrink-0">
        {teardrop ? (
          <svg width="14" height="20" viewBox="0 0 28 40">
            <path
              d="M14 0C6.3 0 0 6.3 0 14c0 9.6 14 26 14 26s14-16.4 14-26C28 6.3 21.7 0 14 0z"
              fill={color}
            />
            <circle cx="14" cy="14" r="5" fill="#fff" />
            {ring && <circle cx="14" cy="14" r="11" fill="none" stroke={ring} strokeWidth="2.5" />}
          </svg>
        ) : (
          <svg width="14" height="14" viewBox="0 0 18 18">
            <circle cx="9" cy="9" r="7" fill={color} stroke="#fff" strokeWidth="2" />
            {ring && <circle cx="9" cy="9" r="8" fill="none" stroke={ring} strokeWidth="1.5" />}
          </svg>
        )}
      </div>
      <span className="text-[var(--ink-2)]">{label}</span>
    </div>
  );
}
